import React from 'react';
import styled from 'styled-components';
import { TaskState } from './TaskStatusBadge';

interface TaskProgressBarProps {
    progress: number; // 0-100
    status: TaskState;
    showPercentage?: boolean;
    size?: 'small' | 'medium' | 'large';
    className?: string;
}

const ProgressBarContainer = styled.div<{ $size: 'small' | 'medium' | 'large' }>`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;

    ${(props) => {
        switch (props.$size) {
            case 'small':
                return 'min-width: 60px;';
            case 'medium':
                return 'min-width: 100px;';
            case 'large':
                return 'min-width: 200px;';
        }
    }}
`;

const ProgressBar = styled.div<{ $size: 'small' | 'medium' | 'large' }>`
    flex: 1;
    background: #e0e0e0;
    border-radius: ${(props) => (props.$size === 'small' ? '3px' : '6px')};
    overflow: hidden;
    height: ${(props) => {
        switch (props.$size) {
            case 'small':
                return '6px';
            case 'medium':
                return '8px';
            case 'large':
                return '12px';
        }
    }};
`;

const ProgressFill = styled.div<{ $progress: number; $status: TaskState }>`
    height: 100%;
    width: ${(props) => props.$progress}%;
    border-radius: inherit;
    transition: width 0.3s ease;

    ${(props) => {
        switch (props.$status) {
            case 'COMPLETED':
            case 'SUCCESS':
                return 'background: #4caf50;';
            case 'ERROR':
                return 'background: #f44336;';
            case 'PARTIAL_SUCCESS':
                return 'background: #ff9800;';
            case 'CANCELLED':
                return 'background: #9e9e9e;';
            default:
                return 'background: #2196f3;';
        }
    }}
`;

const ProgressText = styled.span<{ $size: 'small' | 'medium' | 'large' }>`
    font-size: ${(props) => (props.$size === 'small' ? '12px' : '14px')};
    color: #666;
    min-width: 40px;
    text-align: right;
    font-weight: ${(props) => props.theme.font.medium};
`;

/**
 * TaskProgressBar - Atomic component for displaying task progress
 * Can be used for any type of task progress visualization
 */
export const TaskProgressBar: React.FC<TaskProgressBarProps> = ({ progress, status, showPercentage = true, size = 'medium', className }) => {
    return (
        <ProgressBarContainer $size={size} className={className}>
            <ProgressBar $size={size}>
                <ProgressFill $progress={progress} $status={status} />
            </ProgressBar>
            {showPercentage && <ProgressText $size={size}>{progress}%</ProgressText>}
        </ProgressBarContainer>
    );
};

export default TaskProgressBar;
